/**
 * DevToolsTab — ERP Workspace "Dev Tools" tab.
 * Manifest import (file upload OR URL), full analytics dashboard,
 * scanner script download, API docs, and quick register form.
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  RefreshCw, AlertTriangle, CheckCircle2, Check, X, Zap,
  Upload, Download, Copy, Terminal, FileJson, BookOpen,
  ExternalLink, Link2, BarChart3, ArrowRight,
  FileCheck, FilePlus, FileX, Copy as CopyIcon, Hash,
  FileCode, Search,
} from 'lucide-react';
import { API, authFetch } from '../../lib/api';
import { erpToast } from '../../components/erp/ERPToast';

// ── Analytics KPI tile ────────────────────────────────────────────────────
function KpiTile({ label, value, accent, icon: Icon, sub }) {
  return (
    <div style={{
      flex: '1 1 130px', minWidth: 110, padding: '12px 14px',
      background: 'var(--surface,#fff)', borderRadius: 10,
      border: '1px solid var(--border,#e2e8f0)',
      borderTop: `3px solid ${accent}`,
      boxShadow: '0 1px 4px rgba(0,0,0,.05)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        {Icon && <Icon size={12} color={accent} />}
        <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</span>
      </div>
      <div style={{ fontSize: '1.7rem', fontWeight: 900, color: accent, lineHeight: 1 }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

// ── Collapsible detail table ──────────────────────────────────────────────
function DetailTable({ label, color, items = [], columns = [], emptyMsg = 'None', testId }) {
  const [open, setOpen] = useState(false);
  if (!items.length) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', fontSize: '0.73rem', color: '#94a3b8', borderRadius: 6, border: '1px dashed var(--border)' }}>
      <span style={{ color, fontWeight: 700 }}>0 {label}</span> — {emptyMsg}
    </div>
  );
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: 'var(--surface-2,#f8fafc)', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        <span style={{ fontSize: '0.72rem', fontWeight: 700, color, background: `${color}18`, padding: '1px 8px', borderRadius: 10 }}>
          {items.length} {label}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: '#94a3b8' }}>{open ? '▲ collapse' : '▼ expand'}</span>
      </button>
      {open && (
        <div style={{ overflowX: 'auto', maxHeight: 260 }} data-testid={testId}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
                {columns.map(c => <th key={c} style={{ padding: '5px 10px', textAlign: 'left', fontWeight: 700, color: '#64748b', fontSize: '0.67rem', textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap' }}>{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                  {Object.values(item).map((v, j) => (
                    <td key={j} style={{ padding: '5px 10px', color: '#475569', fontFamily: j > 0 ? 'monospace' : 'inherit', fontSize: '0.71rem', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={String(v || '')}>
                      {String(v || '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Full analytics dashboard ──────────────────────────────────────────────
function ImportAnalytics({ result, onReviewUnmatched, onNavigate, sourceLabel }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }} data-testid="import-analytics">

      {/* Status banner */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
        background: result.unmatched > 0 ? 'rgba(245,158,11,.06)' : 'rgba(34,197,94,.06)',
        border: `1px solid ${result.unmatched > 0 ? 'rgba(245,158,11,.3)' : 'rgba(34,197,94,.3)'}`,
        borderRadius: 10,
      }}>
        {result.unmatched > 0
          ? <AlertTriangle size={16} color="#f59e0b" />
          : <CheckCircle2  size={16} color="#22c55e" />}
        <div>
          <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1e293b' }}>
            Import {result.unmatched > 0 ? 'completed with unmatched rows' : 'successful'}{sourceLabel ? ` — ${sourceLabel}` : ''}
          </div>
          <div style={{ fontSize: '0.73rem', color: '#64748b', marginTop: 2 }}>
            {result.appliedTotal} screens written to DB &nbsp;·&nbsp;
            {result.toCreate} new rows &nbsp;·&nbsp;
            {result.toUpdate} updated &nbsp;·&nbsp;
            {result.failed} failed
          </div>
        </div>
        {result.unmatched > 0 && (
          <button className="erp-btn" onClick={() => { onReviewUnmatched(result.details?.unmatched || []); onNavigate('compmap'); }}
            style={{ marginLeft: 'auto', fontSize: '0.73rem', display: 'flex', alignItems: 'center', gap: 4, background: '#f59e0b', borderColor: '#f59e0b', color: '#fff' }}>
            <ExternalLink size={11} /> Review {result.unmatched} Unmatched
          </button>
        )}
      </div>

      {/* KPI grid */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <KpiTile label="Total in File"     value={result.total}        accent="#64748b"  icon={FileJson}    sub="all entries" />
        <KpiTile label="With Screen ID"    value={result.withId}       accent="#3b82f6"  icon={Hash}        sub="direct writes" />
        <KpiTile label="Without Screen ID" value={result.withoutId}    accent="#8b5cf6"  icon={FileCode}    sub="fuzzy-matched" />
        <KpiTile label="Matched"           value={result.matched}      accent="#06b6d4"  icon={FileCheck}   sub="fuzzy match found" />
        <KpiTile label="Unmatched"         value={result.unmatched}    accent={result.unmatched > 0 ? '#f59e0b' : '#94a3b8'} icon={FileX} sub="no UAMS match" />
        <KpiTile label="New DB Rows"       value={result.toCreate}     accent="#22c55e"  icon={FilePlus}    sub="created" />
        <KpiTile label="Updated Rows"      value={result.toUpdate}     accent="#10b981"  icon={FileCheck}   sub="existing updated" />
        <KpiTile label="Duplicate IDs"     value={result.duplicates}   accent={result.duplicates > 0 ? '#ef4444' : '#94a3b8'} icon={CopyIcon} sub="in manifest" />
        <KpiTile label="Failed"            value={result.failed}       accent={result.failed > 0 ? '#ef4444' : '#94a3b8'}     icon={X}        sub="DB errors" />
      </div>

      {/* Detail tables */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <DetailTable label="Direct Writes (had screenId)" color="#3b82f6" testId="import-detail-direct"
          items={(result.details?.direct || []).map(r => ({ screenId: r.screenId, component: r.componentName, path: r.componentPath }))}
          columns={['Screen ID', 'Component Name', 'Path']} emptyMsg="none" />
        <DetailTable label="Fuzzy Matched" color="#06b6d4" testId="import-detail-matched"
          items={(result.details?.matched || []).map(r => ({ screenId: r.screenId, title: r.screenTitle, component: r.componentName }))}
          columns={['Screen ID', 'Screen Title', 'Component']} emptyMsg="none" />
        <DetailTable label="Unmatched (no UAMS screen found)" color="#f59e0b" testId="import-detail-unmatched"
          items={(result.details?.unmatched || []).map(r => ({ component: r.componentName, path: r.componentPath }))}
          columns={['Component Name', 'Component Path']} emptyMsg="all rows matched" />
        {result.details?.duplicateIds?.length > 0 && (
          <div style={{ padding: '6px 12px', background: 'rgba(239,68,68,.06)', border: '1px solid rgba(239,68,68,.2)', borderRadius: 6, fontSize: '0.73rem', color: '#ef4444' }}>
            <strong>Duplicate Screen IDs in manifest:</strong> {result.details.duplicateIds.join(', ')}
          </div>
        )}
        {result.details?.failed?.length > 0 && (
          <DetailTable label="DB Failures" color="#ef4444" testId="import-detail-failed"
            items={result.details.failed}
            columns={['Screen ID', 'Error']} />
        )}
      </div>

      <details>
        <summary style={{ cursor: 'pointer', fontSize: '0.73rem', color: '#3b82f6', fontWeight: 600 }}>View raw JSON response</summary>
        <pre style={{ background: '#0f172a', color: '#93c5fd', padding: 12, borderRadius: 6, fontSize: '0.67rem', maxHeight: 200, overflow: 'auto', marginTop: 6 }}>
          {JSON.stringify(result, null, 2)}
        </pre>
      </details>
    </div>
  );
}

// ── Screen list for ScreenPickerAssign ───────────────────────────────────
function ScreenList({ screens, selected, onSelect, loading }) {
  if (loading) return (
    <div style={{ padding: '10px 0', textAlign: 'center', fontSize: '0.72rem', color: '#94a3b8' }}>
      <RefreshCw size={12} className="spin" style={{ verticalAlign: 'middle', marginRight: 4 }} /> Loading…
    </div>
  );
  if (!screens.length) return (
    <div style={{ padding: '8px 10px', fontSize: '0.72rem', color: '#94a3b8', fontStyle: 'italic' }}>No screens found.</div>
  );
  return (
    <div style={{ maxHeight: 190, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 6, marginTop: 6 }}>
      {screens.map(s => {
        const isSel = selected?.ScreenId === s.ScreenId;
        const isMapped = !!s.ComponentName;
        return (
          <div key={s.ScreenId} onClick={() => onSelect(s)}
            style={{
              display: 'flex', alignItems: 'center', gap: 7, padding: '5px 10px', cursor: 'pointer',
              background: isSel ? 'rgba(59,130,246,.08)' : 'transparent',
              borderBottom: '1px solid var(--border,#e2e8f0)',
              borderLeft: isSel ? '3px solid #3b82f6' : '3px solid transparent',
              transition: 'background .1s',
            }}>
            <code style={{ fontSize: '0.65rem', color: '#64748b', minWidth: 38, flexShrink: 0 }}>{s.ScreenId}</code>
            <span style={{ fontSize: '0.73rem', flex: 1, fontWeight: isSel ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {s.ScreenTitle || s.ScreenMenuName}
            </span>
            {isMapped && (
              <span style={{ fontSize: '0.6rem', padding: '1px 5px', borderRadius: 3, background: 'rgba(34,197,94,.1)', color: '#16a34a', whiteSpace: 'nowrap', flexShrink: 0 }}>
                mapped
              </span>
            )}
            {s.ScreenType && (
              <span style={{ fontSize: '0.6rem', padding: '1px 5px', borderRadius: 3, background: 'var(--surface-3,#f1f5f9)', color: '#64748b', whiteSpace: 'nowrap', flexShrink: 0 }}>
                {s.ScreenType}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── ScreenPickerAssign — GetInfo-style hierarchical screen lookup ─────────
function ScreenPickerAssign({ onNavigate }) {
  const [pickMode,      setPickMode]      = useState('search'); // 'search' | 'browse'

  // Search mode
  const [searchInput,   setSearchInput]   = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching,     setSearching]     = useState(false);

  // Browse mode
  const [modules,       setModules]       = useState([]);
  const [selModule,     setSelModule]     = useState('');
  const [moduleScreens, setModuleScreens] = useState([]);
  const [selSubModule,  setSelSubModule]  = useState('');
  const [browseLoad,    setBrowseLoad]    = useState(false);

  // Selected screen + assign form
  const [selected,  setSelected]  = useState(null);
  const [compName,  setCompName]  = useState('');
  const [compPath,  setCompPath]  = useState('');
  const [isImpl,    setIsImpl]    = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [lastOk,    setLastOk]    = useState(null);

  useEffect(() => {
    authFetch(`${API}/erp-workspace/admin/modules`)
      .then(r => r.json())
      .then(d => Array.isArray(d) ? setModules(d) : null)
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selModule) { setModuleScreens([]); setSelSubModule(''); return; }
    setBrowseLoad(true);
    authFetch(`${API}/erp-workspace/admin/component-map?moduleId=${selModule}&industryCode=SHARED`)
      .then(r => r.json())
      .then(d => { setModuleScreens(Array.isArray(d) ? d : (d.rows || [])); setSelSubModule(''); })
      .catch(() => {})
      .finally(() => setBrowseLoad(false));
  }, [selModule]);

  const subModules = useMemo(() => {
    const seen = new Map();
    moduleScreens.forEach(s => {
      if (s.SubModuleId && !seen.has(s.SubModuleId))
        seen.set(s.SubModuleId, s.SubModuleName || s.SubModuleId);
    });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [moduleScreens]);

  const browseScreens = selSubModule
    ? moduleScreens.filter(s => s.SubModuleId === selSubModule)
    : moduleScreens;

  const runSearch = async () => {
    if (searchInput.length < 2) return;
    setSearching(true);
    try {
      const params = new URLSearchParams({ search: searchInput.trim(), industryCode: 'SHARED', page: 1, pageSize: 40 });
      const r = await authFetch(`${API}/erp-workspace/admin/component-map?${params}`);
      const d = await r.json();
      setSearchResults(d.rows || (Array.isArray(d) ? d : []));
    } catch (_) {}
    setSearching(false);
  };

  const selectScreen = (row) => {
    setSelected(row);
    setCompName(row.ComponentName || '');
    setCompPath(row.ComponentPath || '');
    setIsImpl(!!row.IsImplemented);
    setLastOk(null);
  };

  const assign = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const r = await authFetch(`${API}/erp-workspace/admin/component-map/${selected.ScreenId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ componentName: compName.trim() || null, componentPath: compPath.trim() || null, isImplemented: isImpl }),
      });
      const d = await r.json();
      if (d.ok) {
        erpToast.success(`Component assigned to ${selected.ScreenId}`);
        setLastOk(selected.ScreenId);
        setSelected(null); setCompName(''); setCompPath(''); setIsImpl(false);
      } else { erpToast.error(d.error || 'Save failed'); }
    } catch (e) { erpToast.error(e.message); }
    setSaving(false);
  };

  const fld = { fontSize: '0.73rem', padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 5, width: '100%' };
  const lbl = { fontSize: '0.66rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em' };

  return (
    <>
      <div style={{ fontWeight: 700, fontSize: '0.83rem', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Search size={13} color="#22c55e" /> Screen Lookup &amp; Component Assign
      </div>

      {/* Mode toggle */}
      <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden', marginBottom: 10, width: 'fit-content' }}>
        {[['search','Search'],['browse','Module Browse']].map(([m, lbl_]) => (
          <button key={m} onClick={() => { setPickMode(m); setSelected(null); }}
            style={{ padding: '4px 12px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', border: 'none',
              background: pickMode === m ? '#22c55e' : 'transparent',
              color: pickMode === m ? '#fff' : '#64748b' }}>
            {lbl_}
          </button>
        ))}
      </div>

      {/* Search mode */}
      {pickMode === 'search' && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 5 }}>
            <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && runSearch()}
              placeholder="Type screen title, ID… (min 2 chars)"
              data-testid="screenpicker-search-input"
              style={{ flex: 1, fontSize: '0.73rem', padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 5 }} />
            <button className="erp-btn" onClick={runSearch} disabled={searching || searchInput.length < 2}
              data-testid="screenpicker-search-btn"
              style={{ fontSize: '0.73rem', display: 'flex', alignItems: 'center', gap: 3 }}>
              {searching ? <RefreshCw size={11} className="spin" /> : <Search size={11} />} Search
            </button>
          </div>
          {searchResults.length > 0 && (
            <div style={{ fontSize: '0.67rem', color: '#64748b', marginTop: 4 }}>
              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} — click a row to select
            </div>
          )}
          <ScreenList screens={searchResults} selected={selected} onSelect={selectScreen} loading={false} />
        </div>
      )}

      {/* Browse mode */}
      {pickMode === 'browse' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <select value={selModule} onChange={e => setSelModule(e.target.value)}
              data-testid="screenpicker-module-select"
              style={{ fontSize: '0.73rem', padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 5, flex: '1 1 150px' }}>
              <option value="">— Select Module —</option>
              {modules.map(m => <option key={m.ModuleId} value={m.ModuleId}>{m.ModuleName}</option>)}
            </select>

            {subModules.length > 0 && (
              <select value={selSubModule} onChange={e => setSelSubModule(e.target.value)}
                data-testid="screenpicker-submodule-select"
                style={{ fontSize: '0.73rem', padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 5, flex: '1 1 150px' }}>
                <option value="">All Sub-Modules ({moduleScreens.length})</option>
                {subModules.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
          </div>
          {selModule && (
            <div style={{ fontSize: '0.67rem', color: '#64748b' }}>
              {browseScreens.length} screen{browseScreens.length !== 1 ? 's' : ''} — click to select
            </div>
          )}
          <ScreenList screens={browseScreens} selected={selected} onSelect={selectScreen} loading={browseLoad} />
        </div>
      )}

      {/* Assignment form */}
      {selected && (
        <div style={{ background: 'rgba(34,197,94,.04)', border: '1px solid rgba(34,197,94,.2)', borderRadius: 8, padding: 12, marginTop: 4 }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#15803d', marginBottom: 8, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <code style={{ background: '#f0fdf4', padding: '1px 6px', borderRadius: 3, color: '#166534' }}>{selected.ScreenId}</code>
            <span style={{ color: '#1e293b' }}>{selected.ScreenTitle}</span>
            {selected.ScreenType && <span style={{ fontSize: '0.63rem', background: '#dcfce7', padding: '1px 5px', borderRadius: 3, color: '#15803d' }}>{selected.ScreenType}</span>}
            {selected.ModuleName && <span style={{ fontSize: '0.63rem', color: '#64748b' }}>· {selected.ModuleName}</span>}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7, marginBottom: 8 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <label style={lbl}>Component Name</label>
              <input value={compName} onChange={e => setCompName(e.target.value)}
                placeholder="SalesInvoice" style={fld} data-testid="screenpicker-comp-name" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <label style={lbl}>Component Path</label>
              <input value={compPath} onChange={e => setCompPath(e.target.value)}
                placeholder="pages/sales/SalesInvoice.jsx" style={fld} data-testid="screenpicker-comp-path" />
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.73rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={isImpl} onChange={e => setIsImpl(e.target.checked)} />
              Mark as Implemented
            </label>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="erp-btn" onClick={() => setSelected(null)} style={{ fontSize: '0.72rem' }}>Cancel</button>
              <button className="erp-btn erp-btn-primary" onClick={assign} disabled={saving}
                data-testid="screenpicker-assign-btn"
                style={{ fontSize: '0.72rem', background: '#22c55e', borderColor: '#22c55e', display: 'flex', alignItems: 'center', gap: 4 }}>
                {saving ? <><RefreshCw size={11} className="spin" /> Saving…</> : <><CheckCircle2 size={11} /> Assign Component</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {lastOk && (
        <div style={{ marginTop: 6, fontSize: '0.71rem', color: '#22c55e', display: 'flex', alignItems: 'center', gap: 5 }}>
          <CheckCircle2 size={11} /> "{lastOk}" assigned.
          <button style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '0.7rem', padding: 0 }}
            onClick={() => onNavigate?.('compmap')}>
            View in Component Map →
          </button>
        </div>
      )}
    </>
  );
}

// ── DevToolsTab ────────────────────────────────────────────────────────────
export function DevToolsTab({ onNavigate, onUnmatched }) {
  // Import mode
  const [importMode,   setImportMode]   = useState('file'); // 'file' | 'url'
  const [importing,    setImporting]    = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError,  setImportError]  = useState(null);
  const [copiedKey,    setCopiedKey]    = useState('');

  // File mode
  const [fileContent,  setFileContent]  = useState(null);
  const [fileName,     setFileName]     = useState('');

  // URL mode
  const [manifestUrl,  setManifestUrl]  = useState('');
  const [urlPreview,   setUrlPreview]   = useState(null);
  const [urlFetching,  setUrlFetching]  = useState(false);

  const copy = (text, key) => { navigator.clipboard.writeText(text); setCopiedKey(key); setTimeout(() => setCopiedKey(''), 2000); };

  const CodeBlock = ({ code, copyKey: ck }) => (
    <div style={{ position: 'relative' }}>
      <pre style={{ background: '#0f172a', color: '#e2e8f0', padding: '10px 12px', borderRadius: 7, fontSize: '0.67rem', overflowX: 'auto', margin: 0, lineHeight: 1.7 }}>{code}</pre>
      <button onClick={() => copy(code, ck)} style={{ position: 'absolute', top: 5, right: 5, background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.15)', borderRadius: 4, color: '#94a3b8', cursor: 'pointer', padding: '2px 7px', fontSize: '0.65rem', display: 'flex', alignItems: 'center', gap: 3 }}>
        {copiedKey === ck ? <><Check size={10} /> Copied</> : <><Copy size={10} /> Copy</>}
      </button>
    </div>
  );

  // ── File import ──────────────────────────────────────────────────────────
  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setImportResult(null); setImportError(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const raw    = ev.target.result;
        const parsed = JSON.parse(raw);
        const screens = Array.isArray(parsed) ? parsed : (parsed.screens || []);
        setFileContent({ screens, meta: { project: parsed.project, generatedAt: parsed.generatedAt } });
      } catch (err) { setImportError(`Cannot parse JSON: ${err.message}`); setFileContent(null); }
    };
    reader.readAsText(file);
  };

  const runFileImport = async () => {
    if (!fileContent?.screens?.length) { setImportError('No screens found in file.'); return; }
    setImporting(true); setImportError(null);
    try {
      const r = await authFetch(`${API}/erp-workspace/admin/import-manifest`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ screens: fileContent.screens }),
      });
      const text = await r.text();
      let d;
      try { d = JSON.parse(text); } catch (_) { throw new Error(`Server returned non-JSON (status ${r.status}). Check body size limit.`); }
      if (d.error) { setImportError(d.error); } else { setImportResult(d); erpToast.success(`Imported ${d.appliedTotal} screens`); }
    } catch (e) { setImportError(e.message); }
    setImporting(false);
  };

  // ── URL import ───────────────────────────────────────────────────────────
  const fetchUrlPreview = async () => {
    if (!manifestUrl.trim()) return;
    setUrlFetching(true); setUrlPreview(null); setImportError(null);
    try {
      const r = await authFetch(`${API}/erp-workspace/admin/fetch-manifest-preview`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: manifestUrl.trim() }),
      });
      const d = await r.json();
      if (d.error) { setImportError(d.error); } else { setUrlPreview(d); }
    } catch (e) { setImportError(e.message); }
    setUrlFetching(false);
  };

  const runUrlImport = async () => {
    if (!manifestUrl.trim()) { setImportError('Enter a manifest URL first.'); return; }
    setImporting(true); setImportError(null);
    try {
      const r = await authFetch(`${API}/erp-workspace/admin/import-manifest-url`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: manifestUrl.trim() }),
      });
      const text = await r.text();
      let d;
      try { d = JSON.parse(text); } catch (_) { throw new Error(`Server returned non-JSON (status ${r.status}).`); }
      if (d.error) { setImportError(d.error); }
      else { setImportResult(d); erpToast.success(`URL import: ${d.appliedTotal} screens written`); }
    } catch (e) { setImportError(e.message); }
    setImporting(false);
  };

  // ── Scanner script ────────────────────────────────────────────────────────
  const SCANNER_SCRIPT = `#!/usr/bin/env node
const fs=require('fs'),path=require('path');
const SRC=path.join(__dirname,'src');
const OUT=path.join(__dirname,'erp-manifest.json');
const SKIP=new Set(['node_modules','.git','dist','build','__tests__']);
const screens=[];
function scan(dir){if(!fs.existsSync(dir))return;for(const e of fs.readdirSync(dir)){const f=path.join(dir,e);if(fs.statSync(f).isDirectory()){if(!SKIP.has(e))scan(f);continue;}if(!/\\.(jsx|js|tsx|ts)$/.test(e))continue;screens.push({componentName:e.replace(/\\.(jsx|js|tsx|ts)$/,''),componentPath:f.replace(SRC+path.sep,'').replace(/\\\\/g,'/'),isImplemented:true});}}
scan(SRC);
fs.writeFileSync(OUT,JSON.stringify({version:'1.0',project:path.basename(__dirname),generatedAt:new Date().toISOString(),screens},null,2));
console.log('Generated:',OUT,'('+screens.length+' components)');`;

  const downloadScript = () => {
    const blob = new Blob([SCANNER_SCRIPT], { type: 'text/javascript' });
    const a    = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: 'erp-screen-scanner.js' });
    a.click();
  };

  const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

  const card = (children, style = {}) => (
    <div style={{ background: 'var(--surface-2,#f8fafc)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, ...style }}>
      {children}
    </div>
  );

  const sectionHead = (Icon, label, color = '#3b82f6') => (
    <div style={{ fontWeight: 700, fontSize: '0.83rem', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
      <Icon size={14} color={color} /> {label}
    </div>
  );

  return (
    <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 18 }}>

      {/* ── Workflow banner ── */}
      <div style={{ background: 'linear-gradient(135deg,#1e293b,#0f172a)', border: '1px solid #334155', borderRadius: 12, padding: '14px 18px', color: '#e2e8f0' }}>
        <div style={{ fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6, color: '#38bdf8', fontSize: '0.88rem' }}>
          <BookOpen size={14} /> ERP Component Registry — Developer Integration Workflow
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: '0.73rem', color: '#94a3b8' }}>
          {[['1','Download scanner','#3b82f6'],['2','Run → manifest.json','#8b5cf6'],['3','Upload file or paste URL','#22c55e'],['4','Review analytics + auto-register in CI','#f59e0b']].map(([n,lbl,c]) => (
            <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,.04)', borderRadius: 7, padding: '5px 10px' }}>
              <span style={{ background: c, color: '#fff', borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 700, flexShrink: 0 }}>{n}</span>
              {lbl}
              {n !== '4' && <ArrowRight size={10} color="#475569" />}
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>

        {/* ── LEFT: Import Section ── */}
        <div style={{ flex: '1 1 420px', display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Import mode toggle */}
          {card(<>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <BarChart3 size={14} color="#8b5cf6" />
              <span style={{ fontWeight: 700, fontSize: '0.83rem' }}>Import Manifest</span>
              <div style={{ marginLeft: 'auto', display: 'flex', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
                {[['file','File Upload'],['url','From URL']].map(([m, lbl]) => (
                  <button key={m} onClick={() => { setImportMode(m); setImportResult(null); setImportError(null); }}
                    style={{ padding: '4px 12px', fontSize: '0.73rem', fontWeight: 700, cursor: 'pointer', background: importMode === m ? '#8b5cf6' : 'transparent', color: importMode === m ? '#fff' : '#64748b', border: 'none' }}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>

            {/* ── File Mode ── */}
            {importMode === 'file' && (
              <>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', border: '2px dashed var(--border)', borderRadius: 8, padding: '12px 14px', background: fileContent ? 'rgba(139,92,246,.04)' : 'transparent', marginBottom: 10, transition: 'all .15s' }}>
                  <FileJson size={20} color={fileContent ? '#8b5cf6' : '#94a3b8'} />
                  <div>
                    <div style={{ fontSize: '0.78rem', fontWeight: 600, color: fileContent ? '#8b5cf6' : '#64748b' }}>
                      {fileName || 'Choose erp-manifest.json…'}
                    </div>
                    {fileContent && <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: 2 }}>
                      {fileContent.screens.length} components · {fileContent.screens.filter(s => s.screenId).length} with screenId · project: {fileContent.meta?.project || 'unknown'}
                    </div>}
                  </div>
                  <input type="file" accept=".json" onChange={onFileChange} style={{ display: 'none' }} data-testid="devtools-manifest-input" />
                </label>
                {importError && <div style={{ fontSize: '0.73rem', color: '#ef4444', marginBottom: 8, display: 'flex', gap: 4 }}><AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 1 }} />{importError}</div>}
                <button className="erp-btn erp-btn-primary" onClick={runFileImport}
                  disabled={!fileContent || importing} data-testid="devtools-import-btn"
                  style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.78rem', background: '#8b5cf6', borderColor: '#8b5cf6', width: '100%', justifyContent: 'center' }}>
                  {importing ? <><RefreshCw size={12} className="spin" /> Importing {fileContent?.screens?.length} screens…</> : <><Upload size={12} /> Import Manifest ({fileContent?.screens?.length || 0} screens)</>}
                </button>
              </>
            )}

            {/* ── URL Mode ── */}
            {importMode === 'url' && (
              <>
                <div style={{ fontSize: '0.73rem', color: '#64748b', marginBottom: 8 }}>
                  Paste the public URL of your <code>erp-manifest.json</code>. The server will fetch and process it.
                </div>
                <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <Link2 size={11} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                    <input value={manifestUrl} onChange={e => { setManifestUrl(e.target.value); setUrlPreview(null); }}
                      placeholder="https://your-cdn.com/erp-manifest.json"
                      data-testid="devtools-url-input"
                      style={{ paddingLeft: 26, width: '100%', fontSize: '0.73rem', padding: '5px 5px 5px 24px', border: '1px solid var(--border)', borderRadius: 6 }} />
                  </div>
                  <button className="erp-btn" onClick={fetchUrlPreview} disabled={urlFetching || !manifestUrl.trim()}
                    data-testid="devtools-url-preview-btn"
                    style={{ fontSize: '0.73rem', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                    {urlFetching ? <RefreshCw size={11} className="spin" /> : <ExternalLink size={11} />} Preview
                  </button>
                </div>
                {urlPreview && (
                  <div style={{ background: 'rgba(59,130,246,.06)', border: '1px solid rgba(59,130,246,.2)', borderRadius: 6, padding: '7px 10px', fontSize: '0.72rem', color: '#1e40af', marginBottom: 8 }}>
                    <CheckCircle2 size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    Fetched OK · <strong>{urlPreview.total}</strong> screens · <strong>{urlPreview.withId}</strong> with screenId · <strong>{urlPreview.withoutId}</strong> without · project: <em>{urlPreview.project || 'unknown'}</em>
                  </div>
                )}
                {importError && <div style={{ fontSize: '0.73rem', color: '#ef4444', marginBottom: 8, display: 'flex', gap: 4 }}><AlertTriangle size={12} style={{ flexShrink: 0 }} />{importError}</div>}
                <button className="erp-btn erp-btn-primary" onClick={runUrlImport}
                  disabled={!manifestUrl.trim() || importing} data-testid="devtools-url-import-btn"
                  style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.78rem', background: '#8b5cf6', borderColor: '#8b5cf6', width: '100%', justifyContent: 'center' }}>
                  {importing ? <><RefreshCw size={12} className="spin" /> Fetching & importing…</> : <><Download size={12} /> Fetch & Import from URL</>}
                </button>
              </>
            )}
          </>)}

          {/* Analytics dashboard */}
          {importResult && (
            <ImportAnalytics
              result={importResult}
              sourceLabel={importMode === 'url' ? manifestUrl.split('/').pop() : fileName}
              onReviewUnmatched={onUnmatched}
              onNavigate={onNavigate}
            />
          )}

          {/* Download scanner */}
          {card(<>
            {sectionHead(Download, 'Download Scanner Script', '#3b82f6')}
            <div style={{ fontSize: '0.73rem', color: '#64748b', marginBottom: 10 }}>
              Drop into your ERP project root → run → get <code>erp-manifest.json</code>:
            </div>
            <CodeBlock code="node erp-screen-scanner.js  # → erp-manifest.json" copyKey="run-cmd" />
            <button className="erp-btn erp-btn-primary" onClick={downloadScript}
              data-testid="devtools-download-scanner"
              style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.75rem', marginTop: 10 }}>
              <Download size={12} /> Download erp-screen-scanner.js
            </button>
          </>)}
        </div>

        {/* ── RIGHT: API Docs + Register ── */}
        <div style={{ flex: '1 1 340px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {card(<>
            {sectionHead(Terminal, 'Register-Screen via CURL', '#f59e0b')}
            <CodeBlock code={`curl -X POST ${API_URL}/api/erp-workspace/register-screen \\
  -H "Authorization: Bearer <YOUR_TOKEN>" \\
  -H "Content-Type: application/json" \\
  -d '{"screenId":"2.1","screenTitle":"Sales Invoice",
       "componentName":"SalesInvoice",
       "componentPath":"pages/sales/SalesInvoice.jsx",
       "isImplemented":true}'`} copyKey="curl-register" />
          </>)}

          {card(<>
            {sectionHead(Terminal, 'Import from URL via CURL', '#8b5cf6')}
            <CodeBlock code={`curl -X POST ${API_URL}/api/erp-workspace/admin/import-manifest-url \\
  -H "Authorization: Bearer <YOUR_TOKEN>" \\
  -H "Content-Type: application/json" \\
  -d '{"url":"https://your-cdn.com/erp-manifest.json"}'`} copyKey="curl-import-url" />
          </>)}

          {card(<>
            {sectionHead(BookOpen, 'API Field Reference', '#64748b')}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.68rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface-3,#f1f5f9)' }}>
                  {['Field','Type','Req?','Description'].map(h => <th key={h} style={{ padding: '4px 8px', textAlign: 'left', fontWeight: 700, color: '#64748b', fontSize: '0.65rem', textTransform: 'uppercase' }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {[
                  ['screenId',       'string',  'No*', 'Unique screen ID — if omitted, fuzzy-matched against UAMS'],
                  ['screenTitle',    'string',  'No*', 'Display title (required for register-screen endpoint)'],
                  ['componentName',  'string',  'No',  'React component name (e.g. SalesInvoice)'],
                  ['componentPath',  'string',  'No',  'Relative path from /src (e.g. pages/sales/SalesInvoice.jsx)'],
                  ['isImplemented',  'boolean', 'No',  'true = component is ready to render'],
                ].map(([f,t,r,d]) => (
                  <tr key={f} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '4px 8px', fontFamily: 'monospace', color: '#3b82f6' }}>{f}</td>
                    <td style={{ padding: '4px 8px', color: '#94a3b8' }}>{t}</td>
                    <td style={{ padding: '4px 8px', color: r === 'Yes' ? '#ef4444' : '#94a3b8', fontWeight: r === 'Yes' ? 700 : 400 }}>{r}</td>
                    <td style={{ padding: '4px 8px', color: '#475569' }}>{d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>)}

          {card(<ScreenPickerAssign onNavigate={onNavigate} />)}
        </div>
      </div>
    </div>
  );
}
